import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { usePostHog } from 'posthog-js/react';
import { useToast } from '../context/ToastContext';
import DashboardTasksList from '../components/DashboardTasksList';
import MatchCard from '../components/MatchCard';
import { Calendar, ChevronRight, Trophy } from 'lucide-react';

interface Match {
  id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  home_away?: string;
  notes?: string;
  lineup_published?: boolean;
  team_id: number;
  team?: {
    id: number;
    name: string;
  };
}

interface LineupConfirmation {
  id: number;
  lineup_id: number;
  player_id: number;
  confirmed_at: string;
  confirmed_by: number;
}

interface LineupPosition {
  id: number;
  match_id: number;
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  confirmations?: LineupConfirmation[] | null;
}

interface PlayerAvailability {
  id: number;
  match_id: number;
  player_id: number;
  availability_status: 'available' | 'unavailable' | 'maybe';
  notes?: string;
  updated_at: string;
}

interface UnsetAvailabilityTask {
  teamId: number;
  teamName: string;
  count: number;
  nextMatchDate: string;
}

interface IncompleteLineupTask {
  teamId: number;
  teamName: string;
  count: number;
  nextMatchDate: string;
}

interface LineupAvailabilityConflictTask {
  matchId: number;
  opponentName: string;
  matchDate: string;
  teamName: string;
}

interface CaptainLineupAvailabilityConflictTask {
  matchId: number;
  opponentName: string;
  matchDate: string;
  teamName: string;
  conflictCount: number;
}

interface UnconfirmedLineupTask {
  matchId: number;
  opponentName: string;
  matchDate: string;
  teamName: string;
  unconfirmedCount: number;
}

interface PendingInvitationTask {
  count: number;
  invitedAt: string;
}

interface OnboardingTask {
  task_id: string;
  title: string;
  description: string;
  action_url: string;
  status: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { teams, loading: teamLoading, setSelectedTeamId, clearSelectedTeam, setShowTeamSelector } = useTeam();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [upcomingMatches, setUpcomingMatches] = useState<Match[]>([]);
  const [matchLineups, setMatchLineups] = useState<{ [matchId: number]: LineupPosition[] }>({});
  const [availability, setAvailability] = useState<Map<number, PlayerAvailability>>(new Map());
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [unsetAvailabilityTasks, setUnsetAvailabilityTasks] = useState<UnsetAvailabilityTask[]>([]);
  const [incompleteLineupTasks, setIncompleteLineupTasks] = useState<IncompleteLineupTask[]>([]);
  const [lineupAvailabilityConflictTasks, setLineupAvailabilityConflictTasks] = useState<LineupAvailabilityConflictTask[]>([]);
  const [captainLineupConflictTasks, setCaptainLineupConflictTasks] = useState<CaptainLineupAvailabilityConflictTask[]>([]);
  const [unconfirmedLineupTasks, setUnconfirmedLineupTasks] = useState<UnconfirmedLineupTask[]>([]);
  const [pendingInvitationTasks, setPendingInvitationTasks] = useState<PendingInvitationTask[]>([]);
  const [onboardingTasks, setOnboardingTasks] = useState<OnboardingTask[]>([]);
  const [dismissingTaskId, setDismissingTaskId] = useState<string | null>(null);
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const [showActiveTeamsOnly, setShowActiveTeamsOnly] = useState(true);
  const toast = useToast();

  useEffect(() => {
    posthog?.capture('dashboard_visited', {
      user_role: user?.role
    });

    const fetchDashboardData = async () => {
      if (!user) {
        setLoadingMatches(false);
        setLoadingTasks(false);
        return;
      }

      const fetchMatches = async () => {
        try {
          setLoadingMatches(true);
          const [matchesResponse, availabilityResponse] = await Promise.all([
            axios.get(`/matches/users/${user.id}/matches`),
            axios.get(`/availability/users/${user.id}/availability`)
          ]);

          const matches = matchesResponse.data;
          const availabilityData = availabilityResponse.data;

          const availabilityMap = new Map<number, PlayerAvailability>();
          availabilityData.forEach((avail: PlayerAvailability) => {
            availabilityMap.set(avail.match_id, avail);
          });
          setAvailability(availabilityMap);

          const now = new Date();

          const upcoming = matches.filter((match: Match) => {
            const matchDate = new Date(match.match_date);
            const matchEndTime = new Date(matchDate.getTime() + 2 * 60 * 60 * 1000);
            return matchEndTime > now;
          }).sort((a: Match, b: Match) =>
            new Date(a.match_date).getTime() - new Date(b.match_date).getTime()
          );

          setUpcomingMatches(upcoming);

          const lineupPromises = upcoming.map((match: Match) =>
            axios.get(`/lineups/matches/${match.id}/lineup`)
              .then(response => ({ matchId: match.id, lineup: response.data }))
              .catch(() => ({ matchId: match.id, lineup: [] }))
          );

          const lineupResults = await Promise.all(lineupPromises);
          const lineupsMap: { [matchId: number]: LineupPosition[] } = {};
          lineupResults.forEach(result => {
            lineupsMap[result.matchId] = result.lineup;
          });
          setMatchLineups(lineupsMap);
        } catch (error) {
          console.error('Failed to fetch matches:', error);
        } finally {
          setLoadingMatches(false);
        }
      };

      const fetchTasks = async () => {
        try {
          setLoadingTasks(true);
          const tasksResponse = await axios.get('/dashboard/tasks', { params: { days_ahead: 90 } });
          const tasks = tasksResponse.data;

          setUnsetAvailabilityTasks(tasks.unsetAvailabilityTasks || []);
          setIncompleteLineupTasks(tasks.incompleteLineupTasks || []);
          setLineupAvailabilityConflictTasks(tasks.lineupAvailabilityConflictTasks || []);
          setCaptainLineupConflictTasks(tasks.captainLineupConflictTasks || []);
          setUnconfirmedLineupTasks(tasks.unconfirmedLineupTasks || []);
          setPendingInvitationTasks(tasks.pendingInvitationTasks || []);

          try {
            const onboardingResponse = await axios.get('/onboarding/tasks');
            setOnboardingTasks(onboardingResponse.data.tasks || []);
          } catch (error) {
            console.error('Failed to fetch onboarding tasks:', error);
            setOnboardingTasks([]);
          }
        } catch (error) {
          console.error('Failed to fetch tasks:', error);
        } finally {
          setLoadingTasks(false);
        }
      };

      await Promise.all([fetchMatches(), fetchTasks()]);
    };

    if (!teamLoading && user) {
      fetchDashboardData();
    }
  }, [teamLoading, posthog, user, teams]);

  const navigateToStrategize = (teamId: number) => {
    setSelectedTeamId(teamId);
    localStorage.setItem('teamManagementActiveTab', 'strategize');
    navigate('/teams/strategize');
  };

  const navigateToTeamManagement = () => {
    clearSelectedTeam();
    setShowTeamSelector(true);
    navigate('/teams/strategize?showTeamSelector=true');
  };

  const totalTaskCount =
    unsetAvailabilityTasks.length +
    incompleteLineupTasks.length +
    lineupAvailabilityConflictTasks.length +
    captainLineupConflictTasks.length +
    unconfirmedLineupTasks.length +
    pendingInvitationTasks.length +
    onboardingTasks.length;

  const hasTasks = totalTaskCount > 0;

  const isTeamActive = (team: { league_start_date?: string; league_end_date?: string }): boolean => {
    const now = new Date();
    const startDate = team.league_start_date ? new Date(team.league_start_date) : null;
    const endDate = team.league_end_date ? new Date(team.league_end_date) : null;

    if (!startDate && !endDate) return true;
    if (startDate && endDate) return now >= startDate && now <= endDate;
    if (startDate) return now >= startDate;
    if (endDate) return now <= endDate;
    return true;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const displayedTeams = showActiveTeamsOnly
    ? teams.filter(team => isTeamActive(team))
    : teams;

  // Skeleton components
  const TaskSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 border-l-4 border-gray-300 dark:border-gray-600 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
        <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    </div>
  );

  const MatchSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-6 animate-pulse">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
          </div>
        </div>
        <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    </div>
  );

  if (teamLoading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">Dashboard</h1>
        </div>

        {/* Tasks and Upcoming Matches in Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
          {/* Tasks Section */}
          <div className={hasTasks ? "order-1 lg:order-2" : "order-2 lg:order-2"}>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100">Tasks</h2>
              {totalTaskCount > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-xs font-semibold text-amber-700 dark:text-amber-300">
                  {totalTaskCount}
                </span>
              )}
            </div>

            {loadingTasks ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <TaskSkeleton key={i} />
                ))}
              </div>
            ) : (
              <DashboardTasksList
                dashboardTasks={{
                  unsetAvailabilityTasks,
                  incompleteLineupTasks,
                  lineupAvailabilityConflictTasks,
                  captainLineupConflictTasks,
                  unconfirmedLineupTasks,
                  pendingInvitationTasks
                }}
                onboardingTasks={onboardingTasks}
                teams={teams}
                onNavigateToStrategize={navigateToStrategize}
                onNavigateToTeamManagement={navigateToTeamManagement}
                onDismissOnboardingTask={async (taskId: string) => {
                  try {
                    setDismissingTaskId(taskId);
                    await axios.post(`/onboarding/tasks/${taskId}/dismiss`);
                    setOnboardingTasks(prev => prev.filter(t => t.task_id !== taskId));
                    toast.success('Task dismissed');
                  } catch (error: any) {
                    console.error('Failed to dismiss task:', error);
                    toast.error(error.response?.data?.detail || 'Failed to dismiss task');
                  } finally {
                    setDismissingTaskId(null);
                  }
                }}
                dismissingTaskId={dismissingTaskId}
                maxTasks={6}
                showExpandButton={true}
                onToggleExpand={() => setTasksExpanded(!tasksExpanded)}
                isExpanded={tasksExpanded}
              />
            )}
          </div>

          {/* Upcoming Matches */}
          <div className={hasTasks ? "order-2 lg:order-1" : "order-1 lg:order-1"}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100">Upcoming Matches</h2>
                {!loadingMatches && upcomingMatches.length > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                    {upcomingMatches.length}
                  </span>
                )}
              </div>
              {upcomingMatches.length > 3 && (
                <Link
                  to="/matches"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-1 transition-colors"
                >
                  View all
                  <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            {loadingMatches ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <MatchSkeleton key={i} />
                ))}
              </div>
            ) : upcomingMatches.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-8 md:p-10 text-center shadow-sm border border-gray-100 dark:border-gray-700/50">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No upcoming matches across all teams
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingMatches.slice(0, 3).map((match) => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    user={user}
                    matchLineups={matchLineups}
                    availability={availability}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* My Teams Section */}
        {teams.length > 0 && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100">
                My Teams
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Active only
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showActiveTeamsOnly}
                  onClick={() => setShowActiveTeamsOnly(!showActiveTeamsOnly)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                    showActiveTeamsOnly ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      showActiveTeamsOnly ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
            {displayedTeams.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-8 md:p-10 text-center shadow-sm border border-gray-100 dark:border-gray-700/50">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {showActiveTeamsOnly
                    ? "No active teams at this time"
                    : "No teams found"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayedTeams.map((team) => (
                  <Link
                    key={team.id}
                    to={`/dashboard/team/${team.id}`}
                    className="group block bg-white dark:bg-gray-800 rounded-xl p-4 md:p-5 shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-md hover:border-primary-200 dark:hover:border-primary-800/50 transition-all cursor-pointer min-h-[44px]"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
                          {team.name}
                        </h3>
                        {(team.league || team.section) && (
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
                            {[team.league, team.section].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {(team.league_start_date || team.league_end_date) && (
                          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            {team.league_start_date && team.league_end_date
                              ? `${formatDate(team.league_start_date)} – ${formatDate(team.league_end_date)}`
                              : team.league_start_date
                              ? `Starts ${formatDate(team.league_start_date)}`
                              : `Ends ${formatDate(team.league_end_date)}`}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 ml-3">
                        <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
